// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// ** Components
import Section from '@/components/common/Section';
import ComponentElementsAccordionCardItem from '@/components/strapi/Elements/ComponentElementsAccordionCardItem';

// ** Types
import type { SxProps, Theme } from '@mui/material/styles';
import type { StrapiComponentProps } from '@/types/strapi';
import type {
	ComponentBlocksAccordionCards as BlocksAccordionCardsProps,
	ComponentElementsAccordionCardItem as ElementsAccordionCardItemProps
} from '@/types/graphql';

type ComponentBlocksAccordionCardsProps = StrapiComponentProps<BlocksAccordionCardsProps> & {
	iconProps?: SxProps<Theme>;
};

const accordionItemKey = (item: ElementsAccordionCardItemProps): string => {
	return `${item.id}::${item.__typename}::${item.accordionCardTitle}`;
};

const ComponentBlocksAccordionCards = ({
	containerSx,
	iconProps,
	accordionCardsItems,
	accordionCardsSectionTitle
}: ComponentBlocksAccordionCardsProps) => {
	const [expandedSet, setExpandedSet] = React.useState<Set<string>>(() => new Set());

	const handleToggle = (key: string) => {
		setExpandedSet((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	};

	const validItems = React.useMemo(() => {
		return (accordionCardsItems || []).filter(
			(item): item is ElementsAccordionCardItemProps => item !== null && item !== undefined
		);
	}, [accordionCardsItems]);

	return (
		<Section containerSx={containerSx}>
			{accordionCardsSectionTitle && (
				<Typography
					variant="title-lg"
					component="h2"
					sx={{
						mb: 10,
						color: 'common.black',
						fontSize: { xs: '2rem', md: '2.5rem' },
						lineHeight: { xs: '2.5rem', md: '3rem' },
						letterSpacing: { xs: '-0.64px', md: '-1.2px' }
					}}
				>
					{accordionCardsSectionTitle}
				</Typography>
			)}

			<Box
				sx={{
					columnCount: { xs: 1, md: 2, lg: 3 },
					columnGap: '1rem'
				}}
			>
				{validItems.map((item) => {
					const key = accordionItemKey(item);
					return (
						<Box key={key} sx={{ breakInside: 'avoid', mb: '1rem' }}>
							<ComponentElementsAccordionCardItem
								id={item.id}
								expanded={expandedSet.has(key)}
								onToggle={() => handleToggle(key)}
								contentId={`accordion-card-panel-${key}`}
								iconProps={iconProps}
								accordionCardDescription={item.accordionCardDescription}
								accordionCardIcon={item.accordionCardIcon}
								accordionCardTitle={item.accordionCardTitle}
								accordionCardBlockEditor={item.accordionCardBlockEditor}
								accordionCardTextContent={item.accordionCardTextContent}
								accordionCardTextVariant={item.accordionCardTextVariant}
								__typename={'ComponentElementsAccordionCardItem'}
							/>
						</Box>
					);
				})}
			</Box>
		</Section>
	);
};

export default ComponentBlocksAccordionCards;
